/**
 * Gmail-style conversation timeline.
 *
 * The thread is one connected card: every message is a collapsible row with
 *   - Persona header (avatar, sender name, snippet when collapsed, time + age)
 *   - A "to <recipient> ▾" line that expands full From / To / Date / Direction
 *   - Message content (HTML rendered safely, quoted replies folded away)
 *   - Attachments grid (if any)
 *   - Status / delivery indicator
 *   - Per-message Reply / Forward actions (plus an overflow menu)
 *
 * Reply and Forward open an inline composer at the head of the thread — the
 * newest message sorts to the top — instead of a separate drawer.
 */
import { useEffect, useMemo, useRef, useState, type JSX } from 'react'
import DOMPurify from 'dompurify'
import {
  ChevronRight,
  ChevronDown,
  Check,
  CheckCheck,
  CheckCircle2,
  AlertCircle,
  Copy,
  CornerUpLeft,
  CornerUpRight,
  MoreVertical,
  ShieldQuestion,
  ShieldCheck,
  Eye,
  FileText,
  Lock,
  Paperclip,
  ArrowLeftRight,
  Mail,
  Loader2,
  Star,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type {
  ActivityLogEntry,
  ConversationDetailResponse,
  Message,
  MessageAttachment,
  MessageStatus,
} from '../../../modules/conversations/types.js'
import { formatDateTime, timestampMs } from '../../../utils/timezone.js'
import { R, formatRelative, htmlToText } from './primitives.js'
import InlineComposer, { canForwardChannel, type ComposerMode } from './InlineComposer.js'
import SuggestedReply from './ai/SuggestedReply.js'
import { PortalFormSubmissionCard, isPortalFormSubmission } from './PortalFormSubmissionCard.js'
import { plainToHtml } from './ai/text.js'
import TranslatableContent from './TranslatableContent.js'

type Conversation = ConversationDetailResponse['conversation']

function formatSize(bytes: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function cleanTimelineMessageBody(body: string | null | undefined): string {
  if (!body) return ''
  if (body.includes('Form submitted —') || /(?:^|\n)[ \t]*(?:conversation\s*(?:id|ref|reference)?|ticket\s*(?:id|ref|reference)?)\s*:/i.test(body)) {
    return body
      .replace(/(?:^|\n)[ \t]*(?:conversation\s*(?:id|ref|reference)?|ticket\s*(?:id|ref|reference)?)\s*:[^\n]*(?:\n|$)/gi, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  }
  return body
}

/** Delivery status, as one quiet pill — tone carries the meaning, not weight. */
const STATUS_PILL: Partial<
  Record<MessageStatus, { label: string; tone: string; icon: LucideIcon; spin?: boolean; hint?: string }>
> = {
  // In-flight states share a spinner: a static clock reads as "scheduled",
  // not "working on it", so a fast send looked like it had already finished.
  sending: { label: 'Sending', tone: 'bg-slate-50 text-slate-500 ring-slate-200/70', icon: Loader2, spin: true },
  queued: { label: 'Sending', tone: 'bg-slate-50 text-slate-500 ring-slate-200/70', icon: Loader2, spin: true },
  sent: {
    label: 'Sent',
    tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200/60',
    icon: Check,
    hint: 'Handed to the provider',
  },
  delivered: {
    label: 'Delivered',
    tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200/60',
    icon: CheckCheck,
    hint: 'Reached the recipient',
  },
  read: {
    label: 'Read',
    tone: 'bg-sky-50 text-sky-700 ring-sky-200/60',
    icon: CheckCheck,
    hint: 'Opened by the recipient',
  },
  failed: { label: 'Failed', tone: 'bg-red-50 text-red-700 ring-red-200/60', icon: AlertCircle },
  bounced: { label: 'Failed', tone: 'bg-red-50 text-red-700 ring-red-200/60', icon: AlertCircle },
  pending_approval: {
    label: 'Pending approval',
    tone: 'bg-amber-50 text-amber-700 ring-amber-200/60',
    icon: ShieldQuestion,
  },
}

function StatusBadge({ status }: { status: MessageStatus }): JSX.Element | null {
  const pill = STATUS_PILL[status]
  if (!pill) return null
  const Icon = pill.icon
  return (
    <span
      title={pill.hint ?? pill.label}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-[1px] text-[10px] font-medium ring-1 ${pill.tone}`}
    >
      <Icon className={`h-3 w-3 ${pill.spin ? 'animate-spin' : ''}`} /> {pill.label}
    </span>
  )
}

function AttachmentChip({ att, onClick }: { att: MessageAttachment; onClick?: () => void }): JSX.Element {
  const isImage = att.mimeType.startsWith('image/') && att.url
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex items-center gap-2.5 ${R} border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-all hover:border-[#3366FF]/60 hover:bg-[#F7F9FF] hover:shadow-[0_1px_4px_rgba(51,102,255,0.12)]`}
    >
      {isImage ? (
        <img src={att.url} alt={att.filename} className={`h-8 w-8 ${R} object-cover`} />
      ) : (
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center ${R} bg-slate-50 group-hover:bg-blue-50`}>
          <FileText className="h-4 w-4 text-slate-400 group-hover:text-[#3366FF]" />
        </span>
      )}
      <span className="flex min-w-0 flex-col items-start leading-tight">
        <span className="max-w-[180px] truncate font-medium text-slate-700">{att.filename}</span>
        {att.sizeBytes > 0 && <span className="text-[10px] text-slate-400">{formatSize(att.sizeBytes)}</span>}
      </span>
      <Eye className="h-3.5 w-3.5 shrink-0 text-slate-300 group-hover:text-[#3366FF]" />
    </button>
  )
}

function ApprovalEventCard({ entry }: { entry: ActivityLogEntry }): JSX.Element {
  const isRequested = entry.eventType.includes('approval_requested')
  const d = entry.details
  const levelName = typeof d['levelName'] === 'string' ? d['levelName'] : null
  const stateName = typeof d['stateName'] === 'string' ? d['stateName'] : null
  const approverName = typeof d['approverName'] === 'string' ? d['approverName'] : null
  const senderRemark = typeof d['senderRemark'] === 'string' ? d['senderRemark'] : null
  const reviewRemark = typeof d['remark'] === 'string' ? d['remark'] : null
  const actorName = typeof d['actorName'] === 'string' ? d['actorName'] : null

  const Icon = isRequested ? ShieldCheck : CheckCircle2
  const iconBg = isRequested ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'
  const label = isRequested ? 'Sent for Approval' : 'Review Submitted'

  return (
    <div className="bg-slate-50/60">
      <div className="flex items-center gap-3.5 px-5 py-3.5">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconBg}`}>
            <Icon className="h-5 w-5" strokeWidth={1.8} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-slate-800">{label}</span>
              {stateName && (
                <span className={`inline-flex items-center rounded-full px-2 py-[1px] text-[10px] font-medium ${iconBg}`}>
                  {stateName}
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[11px] text-slate-500">
              {levelName && <span><span className="font-medium">Level:</span> {levelName}</span>}
              {stateName && <span><span className="font-medium">State:</span> {stateName}</span>}
              {approverName && <span><span className="font-medium">Approver:</span> {approverName}</span>}
            </div>
            {senderRemark && (
              <p className="mt-1 text-xs text-slate-500"><span className="font-medium">Sender Remark:</span> {senderRemark}</p>
            )}
            {reviewRemark && (
              <p className="mt-1 text-xs text-slate-500"><span className="font-medium">Review Remark:</span> {reviewRemark}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="whitespace-nowrap text-[11px] text-slate-400">{formatDateTime(entry.createdAt)}</span>
            {actorName && <span className="text-[10px] text-slate-400">by {actorName}</span>}
          </div>
        </div>
    </div>
  )
}

/**
 * MessageBody — renders email HTML with proper styling.
 *
 * Email messages often contain quoted replies wrapped in <blockquote>,
 * .gmail_quote, .moz-cite-prefix, or Outlook's <div style="...border...">.
 * This component:
 *   1. Splits the HTML into "new content" and "quoted reply" sections
 *   2. Renders the new content prominently
 *   3. Collapses quoted replies behind a "Show quoted content" toggle
 *   4. Applies prose-like styling to common email HTML elements
 */
function MessageBody({ html }: { html: string }): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null)
  const [showQuoted, setShowQuoted] = useState(false)
  const [hasQuoted, setHasQuoted] = useState(false)

  // Detect if content is plain text (no HTML tags) — if so, convert line breaks
  // to <br> and escape HTML entities for safe rendering
  const isPlainText = !/<[a-z][\s\S]*>/i.test(html)
  const safeHtml = isPlainText
    ? html
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br />')
    : DOMPurify.sanitize(html, {
        // <style>/<link>/<meta>/<base>: email CSS leaks into the parent page
        // and can hijack relative URLs. class/id attrs: email classes and ids
        // can match page-level CSS/elements. Handlers, javascript: URLs,
        // scripts, iframes, SVG tricks are stripped by DOMPurify's defaults —
        // this covers attacker-controlled bodies (inbound mail, portal-form
        // submissions) as well as benign markup.
        FORBID_TAGS: ['style', 'link', 'meta', 'base', 'html', 'head', 'body', 'title'],
        FORBID_ATTR: ['class', 'id'],
      })

  useEffect(() => {
    if (!containerRef.current) return
    const container = containerRef.current

    // Find quoted content elements
    const quotedSelectors = [
      'blockquote',
      'div[style*="border:none;border-top:solid"]',
      'div[style*="border-top"]',
      'div[style*="border-left"]',
      'div[style*="padding-left"]',
    ]
    const quotedEls: HTMLElement[] = []
    for (const sel of quotedSelectors) {
      container.querySelectorAll<HTMLElement>(sel).forEach((el) => {
        // Only include if it has meaningful content
        if (el.textContent && el.textContent.trim().length > 10) {
          quotedEls.push(el)
        }
      })
    }

    if (quotedEls.length > 0) {
      setHasQuoted(true)
      // Wrap each quoted element in a collapsible wrapper
      for (const el of quotedEls) {
        if (el.parentElement && !el.parentElement.classList.contains('kc3-quoted-wrapper')) {
          const wrapper = document.createElement('div')
          wrapper.className = 'kc3-quoted-wrapper'
          wrapper.style.display = showQuoted ? 'block' : 'none'
          el.parentElement.insertBefore(wrapper, el)
          wrapper.appendChild(el)
        } else if (el.parentElement?.classList.contains('kc3-quoted-wrapper')) {
          el.parentElement.style.display = showQuoted ? 'block' : 'none'
        }
      }
    } else {
      setHasQuoted(false)
    }
  }, [html, showQuoted])

  return (
    <div>
      {/* Quoted content toggle */}
      {hasQuoted && (
        <button
          type="button"
          onClick={() => setShowQuoted((v) => !v)}
          className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700"
        >
          <ChevronRight className={`h-3 w-3 transition-transform ${showQuoted ? 'rotate-90' : ''}`} />
          {showQuoted ? 'Hide quoted content' : 'Show quoted content'}
        </button>
      )}

      {/* Message HTML content */}
      <div
        ref={containerRef}
        className="kc3-message-html text-sm leading-relaxed text-slate-800"
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />

      {/* Inline CSS for email HTML elements — scoped under .kc3-message-html */}
      <style>{`
        .kc3-message-html p { margin: 0 0 0.5em 0; }
        .kc3-message-html p:last-child { margin-bottom: 0; }
        .kc3-message-html br + br { display: block; margin-top: 0.25em; }
        .kc3-message-html ul, .kc3-message-html ol { margin: 0.5em 0; padding-left: 1.5em; }
        .kc3-message-html li { margin: 0.15em 0; }
        .kc3-message-html a { color: #3366FF; text-decoration: underline; }
        .kc3-message-html a:hover { text-decoration: none; }
        .kc3-message-html img { max-width: 100%; height: auto; border-radius: 3px; }
        .kc3-message-html table { max-width: 100%; border-collapse: collapse; }
        .kc3-message-html td, .kc3-message-html th { padding: 4px 8px; border: 1px solid #e5e7eb; }
        .kc3-message-html pre { background: #f3f4f6; border-radius: 3px; padding: 8px 12px; overflow-x: auto; font-size: 12px; }
        .kc3-message-html code { background: #f3f4f6; border-radius: 2px; padding: 1px 4px; font-size: 12px; }
        .kc3-message-html hr { border: none; border-top: 1px solid #e5e7eb; margin: 0.75em 0; }
        .kc3-message-html h1, .kc3-message-html h2, .kc3-message-html h3 { font-size: 1em; font-weight: 600; margin: 0.5em 0 0.25em; }
        .kc3-message-html strong, .kc3-message-html b { font-weight: 600; }
        .kc3-message-html .kc3-quoted-wrapper blockquote {
          margin: 0.5em 0;
          padding: 0.5em 1em;
          border-left: 3px solid #d1d5db;
          color: #6b7280;
          font-size: 12px;
          background: #f9fafb;
          border-radius: 0 3px 3px 0;
        }
        .kc3-message-html blockquote:not(.kc3-quoted-wrapper blockquote) {
          margin: 0.5em 0;
          padding: 0.5em 1em;
          border-left: 3px solid #d1d5db;
          color: #6b7280;
          font-size: 12px;
          background: #f9fafb;
          border-radius: 0 3px 3px 0;
        }
      `}</style>
    </div>
  )
}

/**
 * Who wrote a message, from the viewer's perspective. The avatar colour carries
 * this — no badges — so a client message is never mistaken for an agent's.
 */
type SenderRole = 'client' | 'agent' | 'you'

/**
 * Gmail draws thread avatars as solid circles with a white initial. Each role
 * gets its own hue family; within a family the exact shade is picked from the
 * sender's name, so two different agents still look different from each other.
 */
const ROLE_AVATAR_COLORS: Record<SenderRole, string[]> = {
  // Clients — warm violet / magenta.
  client: ['bg-[#8E44AD]', 'bg-[#7B1FA2]', 'bg-[#C2185B]', 'bg-[#AD1457]'],
  // Other agents — cool teal / green.
  agent: ['bg-[#00897B]', 'bg-[#0B8043]', 'bg-[#00838F]', 'bg-[#2E7D32]'],
  // You — always the brand blue, so your own replies are instantly scannable.
  you: ['bg-[#3366FF]'],
}

function ThreadAvatar({ name, role }: { name: string; role: SenderRole }): JSX.Element {
  const palette = ROLE_AVATAR_COLORS[role]
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  const color = palette[Math.abs(hash) % palette.length]!
  const initial = (name.trim()[0] ?? '?').toUpperCase()
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${color}`}
      aria-hidden="true"
    >
      {initial}
    </span>
  )
}

/**
 * MessageActions — the Gmail action cluster on a message row: Reply, Forward
 * and an overflow menu. Icons stay muted until the row is hovered or open.
 */
function MessageActions({
  canReply,
  allowForward,
  onReply,
  onForward,
  onCopy,
}: {
  canReply: boolean
  allowForward: boolean
  onReply: () => void
  onForward: () => void
  onCopy: () => void
}): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  const iconBtn = 'rounded-full p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800'

  return (
    <div ref={ref} className="relative flex items-center gap-0.5">
      {canReply && (
        <button type="button" onClick={onReply} title="Reply" aria-label="Reply" className={iconBtn}>
          <CornerUpLeft className="h-4 w-4" />
        </button>
      )}
      {canReply && allowForward && (
        <button type="button" onClick={onForward} title="Forward" aria-label="Forward" className={iconBtn}>
          <CornerUpRight className="h-4 w-4" />
        </button>
      )}
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        title="More"
        aria-label="More actions"
        aria-expanded={menuOpen}
        className={iconBtn}
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {menuOpen && (
        <div
          className={`absolute right-0 top-full z-20 mt-1 w-44 overflow-hidden ${R} border border-slate-200 bg-white py-1 shadow-lg`}
        >
          {canReply && (
            <button
              type="button"
              onClick={() => { setMenuOpen(false); onReply() }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
            >
              <CornerUpLeft className="h-3.5 w-3.5 text-slate-400" /> Reply
            </button>
          )}
          {canReply && allowForward && (
            <button
              type="button"
              onClick={() => { setMenuOpen(false); onForward() }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
            >
              <CornerUpRight className="h-3.5 w-3.5 text-slate-400" /> Forward
            </button>
          )}
          <button
            type="button"
            onClick={() => { setMenuOpen(false); onCopy() }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
          >
            <Copy className="h-3.5 w-3.5 text-slate-400" /> Copy message text
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * ThreadMessage — one message rendered as a Gmail-style collapsible row inside
 * the shared thread container.
 *   - Collapsed: avatar + sender + a one-line plain-text snippet + time.
 *   - Expanded:  avatar + sender + a "to <recipient> ▾" line (which unfolds the
 *                full From / To / Date / Direction detail grid), then the HTML
 *                body, any attachments and the Reply / Forward actions.
 * The newest message is expanded by default; older ones start collapsed.
 */
function ThreadMessage({
  message,
  conversation,
  currentAgentId,
  defaultOpen,
  onAttachmentSelect,
  canReply,
  allowForward,
  onReply,
  onForward,
  ratingScale,
}: {
  message: Message
  conversation: Conversation
  currentAgentId: string | null
  defaultOpen: boolean
  onAttachmentSelect?: ((attachmentId: string) => void) | undefined
  canReply: boolean
  allowForward: boolean
  onReply: (m: Message) => void
  onForward: (m: Message) => void
  ratingScale: number
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen)
  const [showDetails, setShowDetails] = useState(false)

  const isInternal = message.visibility === 'internal'
  const isOutbound = message.direction === 'outbound'
  const isOwnAgent = isOutbound && message.senderAgentId === currentAgentId

  const resolvedAgentName = message.senderAgentName || 'Agent'
  const customerName = message.senderName || message.senderHandle || 'Customer'

  const senderLabel = isOutbound ? (isOwnAgent ? 'You' : resolvedAgentName) : customerName
  const role: SenderRole = isOutbound ? (isOwnAgent ? 'you' : 'agent') : 'client'

  /* Gmail's "to me" / "to Nikhil" line — prefer the envelope recipients saved
     on an outbound email. Fall back to the conversation contact for older
     messages and non-email channels. */
  const recipientLabel = isInternal
    ? 'internal team'
    : isOutbound
      ? message.toRecipients?.length
        ? message.toRecipients.join(', ')
        : conversation.displayName || conversation.primaryHandle || 'customer'
      : 'me'

  const cleanedBody = useMemo(() => cleanTimelineMessageBody(message.body), [message.body])
  const displayMessage = useMemo(() => ({ ...message, body: cleanedBody }), [message, cleanedBody])

  const isFormSubmission = useMemo(
    () => isPortalFormSubmission(message.portalForm, message.body),
    [message.portalForm, message.body],
  )

  const snippet = useMemo(() => {
    if (isFormSubmission) {
      return `Form submitted: ${message.portalForm?.formName || 'Portal Form'}`
    }
    const text = htmlToText(cleanedBody).replace(/\s+/g, ' ').trim()
    return text.length > 160 ? `${text.slice(0, 160)}…` : text
  }, [cleanedBody, isFormSubmission, message.portalForm?.formName])

  const hasAttachments = message.attachments.length > 0
  const relative = formatRelative(message.createdAt)

  const copyText = (): void => {
    void navigator.clipboard?.writeText(htmlToText(cleanedBody))
  }

  /** Left gutter that keeps every line under a message aligned with its name
      (row padding 20px + 36px avatar + 14px gap). */
  const bodyIndent = 'pl-[70px] pr-5'

  return (
    <div
      className={`group transition-colors ${
        isInternal ? 'bg-amber-50/40' : open ? 'bg-white' : 'bg-white hover:bg-slate-50/60'
      }`}
    >
      {/* Header row — the left region toggles the message, the right region
          holds status, timestamp and the Reply / Forward actions. */}
      <div className="flex items-start gap-3.5 px-5 pt-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-start gap-3.5 text-left"
        >
          <ThreadAvatar name={senderLabel} role={role} />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-[13px] font-semibold tracking-[-0.01em] text-slate-900">
                {senderLabel}
              </span>
              {isInternal && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-amber-200/70">
                  <Lock className="h-2.5 w-2.5" /> Private
                </span>
              )}
            </div>
            {/* Collapsed rows preview the body on the second line, exactly where
                an open message shows its "to <recipient>" line. */}
            {!open && snippet && (
              <div className="mt-0.5 truncate text-[13px] text-slate-500">{snippet}</div>
            )}
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-2 pt-0.5">
          {hasAttachments && <Paperclip className="h-3.5 w-3.5 text-slate-400" />}
          <StatusBadge status={message.status} />
          {message.csatScore != null && (
            <span
              title={message.csatSubmittedAt ? `Rated ${formatDateTime(message.csatSubmittedAt)}` : 'CSAT'}
              className="inline-flex items-center gap-1 rounded bg-teal-50 px-2 py-[1px] text-[10px] font-medium text-teal-700"
            >
              <Star className="h-3 w-3 fill-teal-600 text-teal-600" />
              {message.csatScore}/{ratingScale}
            </span>
          )}
          <span className="hidden whitespace-nowrap text-xs text-slate-500 sm:inline">
            {formatDateTime(message.createdAt)}
            {relative && <span className="ml-1 text-slate-400">({relative})</span>}
          </span>
          {/* Actions stay hidden on quiet rows and surface on hover / focus —
              always visible on the open message, as in Gmail. */}
          <div
            className={`transition-opacity focus-within:opacity-100 group-hover:opacity-100 ${open ? 'opacity-100' : 'opacity-0'}`}
          >
            <MessageActions
              canReply={canReply}
              allowForward={allowForward}
              onReply={() => onReply(message)}
              onForward={() => onForward(message)}
              onCopy={copyText}
            />
          </div>
        </div>
      </div>

      {/* "to <recipient> ▾" — expands the full envelope detail, like Gmail. */}
      {open && (
        <div className={`${bodyIndent} -mt-4`}>
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            aria-expanded={showDetails}
            className="inline-flex max-w-full items-center gap-0.5 break-all text-left text-xs text-slate-500 transition-colors hover:text-slate-700"
          >
            to {recipientLabel}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
          </button>
          {showDetails && (
            <dl className={`mt-2 inline-block ${R} border border-slate-200 bg-slate-50/70 px-3 py-2 text-[11px] text-slate-600`}>
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-slate-400">from</dt>
                <dd className="min-w-0 break-all">
                  {senderLabel}
                  {message.senderHandle ? ` <${message.senderHandle}>` : ''}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-slate-400">to</dt>
                <dd className="min-w-0 break-all">{recipientLabel}</dd>
              </div>
              {message.ccRecipients?.length ? (
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-slate-400">cc</dt>
                  <dd className="min-w-0 break-all">{message.ccRecipients.join(', ')}</dd>
                </div>
              ) : null}
              {message.bccRecipients?.length ? (
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-slate-400">bcc</dt>
                  <dd className="min-w-0 break-all">{message.bccRecipients.join(', ')}</dd>
                </div>
              ) : null}
              {message.emailSubject ? (
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-slate-400">subject</dt>
                  <dd className="min-w-0 break-all">{message.emailSubject}</dd>
                </div>
              ) : null}
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-slate-400">date</dt>
                <dd>{formatDateTime(message.createdAt)}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-16 shrink-0 text-slate-400">direction</dt>
                <dd className="inline-flex items-center gap-1">
                  {isOutbound ? <ArrowLeftRight className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                  {isOutbound ? 'Outbound' : 'Inbound'}
                </dd>
              </div>
              {message.mergedFromReferenceId && (
                <div className="flex gap-2">
                  <dt className="w-16 shrink-0 text-slate-400">merged from</dt>
                  <dd>{message.mergedFromReferenceId}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      )}

      {/* Expanded body — aligned under the sender name */}
      <div className={`${bodyIndent} ${open ? 'pb-5 pt-3' : 'pb-4'}`}>
        {open && (
          <>
            {isFormSubmission ? (
              <PortalFormSubmissionCard
                formName={message.portalForm?.formName}
                formDescription={message.portalForm?.formDescription}
                fields={
                  message.portalForm?.fields && message.portalForm.fields.length > 0
                    ? message.portalForm.fields.map((f) => ({
                        id: f.id,
                        label: f.label,
                        type: f.type,
                        required: f.required,
                        helpText: f.helpText,
                        placeholder: f.placeholder,
                        options: f.options,
                        value: message.portalForm?.values?.[f.id],
                      }))
                    : undefined
                }
                rawBody={message.body}
                attachments={message.attachments}
                onAttachmentClick={onAttachmentSelect}
              />
            ) : (
              <TranslatableContent
                message={displayMessage}
                renderOriginal={(html) => <MessageBody html={html} />}
              />
            )}

            {hasAttachments && !isFormSubmission && (
              <div className="mt-3">
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Attachments ({message.attachments.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {message.attachments.map((a) => (
                    <AttachmentChip key={a.id} att={a} onClick={() => onAttachmentSelect?.(a.id)} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/** Gmail's outlined pill button, used for the Reply / Forward thread actions. */
function ThreadActionButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof CornerUpLeft
  label: string
  onClick: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-1.5 text-[13px] font-medium text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50 hover:shadow-sm"
    >
      <Icon className="h-4 w-4 text-slate-500" />
      {label}
    </button>
  )
}

export interface ConversationTimelineProps {
  conversation: Conversation
  messages: Message[]
  activityLog?: ActivityLogEntry[]
  currentAgentId: string | null
  emptyLabel?: string
  onAttachmentSelect?: ((attachmentId: string) => void) | undefined
  /** Whether the viewer may send on this conversation (assignee/collaborator). */
  canReply?: boolean | undefined
  appendMessage?: ((m: Message) => void) | undefined
  replaceMessage?: ((oldId: string, m: Message) => void) | undefined
  markMessageFailed?: ((id: string, error: string) => void) | undefined
  ratingScale: number
}

export default function ConversationTimeline({
  conversation,
  messages,
  activityLog,
  currentAgentId,
  emptyLabel = 'Messages in this conversation will appear here.',
  onAttachmentSelect,
  canReply = false,
  appendMessage,
  replaceMessage,
  markMessageFailed,
  ratingScale,
}: ConversationTimelineProps): JSX.Element {
  const topRef = useRef<HTMLDivElement>(null)
  // `draft` is set only when the composer was opened by accepting the AI
  // suggested reply, so it pre-fills the editor.
  const [composer, setComposer] = useState<{
    mode: ComposerMode
    source: Message | null
    draft?: string
  } | null>(null)

  // The composer can only send when the panel wired up the message-store
  // callbacks; without them the actions would be dead buttons.
  const composerHandlers =
    appendMessage && replaceMessage && markMessageFailed
      ? { appendMessage, replaceMessage, markMessageFailed }
      : null
  const canCompose = canReply && composerHandlers !== null
  const allowForward = canForwardChannel(conversation.channelOrigin)

  // Auto-scroll to the newest message (the top of the thread), but never yank
  // the view away from an open draft.
  useEffect(() => {
    if (composer) return
    topRef.current?.scrollIntoView({ block: 'start' })
  }, [messages.length, composer])

  const approvalEvents = useMemo(
    () => (activityLog ?? []).filter(
      (e) => e.eventType.includes('approval_requested') || e.eventType.includes('approval_decided'),
    ),
    [activityLog],
  )

  type TimelineItem =
    | { kind: 'message'; id: string; createdAt: string; message: Message }
    | { kind: 'approval'; id: string; createdAt: string; entry: ActivityLogEntry }

  const merged: TimelineItem[] = useMemo(() => {
    const items: TimelineItem[] = []
    for (const m of messages) {
      items.push({ kind: 'message', id: m.id, createdAt: m.createdAt, message: m })
    }
    for (const e of approvalEvents) {
      items.push({ kind: 'approval', id: e.id, createdAt: e.createdAt, entry: e })
    }
    // Newest → oldest — the latest message lands on top so the agent never
    // has to scroll to reach the active end of the conversation. Sort
    // ascending then reverse rather than sorting descending: on equal
    // timestamps the item that arrived later in the input still wins.
    items.sort((a, b) => timestampMs(a.createdAt) - timestampMs(b.createdAt)).reverse()
    return items
  }, [messages, approvalEvents])

  // The newest message starts expanded; everything below it collapses to a row.
  const newestMessageIndex = useMemo(
    () => merged.findIndex((it) => it.kind === 'message'),
    [merged],
  )

  /** The message a bare Reply / Forward should act on. */
  const newestMessage = useMemo<Message | null>(() => {
    const item = newestMessageIndex >= 0 ? merged[newestMessageIndex] : undefined
    return item?.kind === 'message' ? item.message : null
  }, [merged, newestMessageIndex])

  const openComposer = (mode: ComposerMode, source: Message | null, draft?: string): void => {
    setComposer(draft === undefined ? { mode, source } : { mode, source, draft })
  }

  /**
   * A reply is only worth drafting when the customer spoke last. Suggesting a
   * response to the agent's own message is nonsense, and each draft is a real
   * model call, so this gate keeps the cost tied to actual need.
   */
  const awaitingReply = newestMessage?.direction === 'inbound'
  const [suggestionDismissed, setSuggestionDismissed] = useState(false)

  if (messages.length === 0 && approvalEvents.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-400">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-blue-50">
          <Mail className="h-10 w-10 text-[#3366FF]/70" strokeWidth={1.5} />
        </div>
        <p className="text-sm font-semibold text-slate-500">Nothing here yet…</p>
        <p className="max-w-xs text-center text-xs text-slate-400">{emptyLabel}</p>
        {canCompose && (
          <ThreadActionButton icon={CornerUpLeft} label="Reply" onClick={() => openComposer('reply', null)} />
        )}
        {canReply && composerHandlers && composer && (
          <div className="w-full max-w-2xl px-4">
            <InlineComposer
              key={`${composer.mode}-${composer.source?.id ?? 'new'}`}
              conversation={conversation}
              currentAgentId={currentAgentId}
              mode={composer.mode}
              sourceMessage={composer.source}
              onCancel={() => setComposer(null)}
              onSent={() => setComposer(null)}
              {...composerHandlers}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="px-5 py-5">
      <div ref={topRef} />

      {/* Reply / Forward sit above the thread — the newest message is the top
          row, so replying never means scrolling to the bottom. The composer
          replaces the actions once opened. */}
      {canReply &&
        composerHandlers &&
        (composer ? (
          <div className="mb-3">
            <InlineComposer
              key={`${composer.mode}-${composer.source?.id ?? 'new'}`}
              conversation={conversation}
              currentAgentId={currentAgentId}
              mode={composer.mode}
              sourceMessage={composer.source}
              {...(composer.draft !== undefined ? { initialMessage: composer.draft } : {})}
              onCancel={() => setComposer(null)}
              onSent={() => setComposer(null)}
              {...composerHandlers}
            />
          </div>
        ) : (
          <div className="mb-3">
            {/* AI draft of the next reply, directly above the actions — the
                agent sees a ready suggestion instead of a blank composer. */}
            {canCompose && !suggestionDismissed && (
              <div className="mb-4">
                <SuggestedReply
                  conversationId={conversation.id}
                  enabled={awaitingReply}
                  // The endpoint returns plain text; the composer holds HTML. Without
                  // this every blank line collapses and the draft arrives as one
                  // run-on paragraph.
                  onUse={(draft) => openComposer('reply', newestMessage, plainToHtml(draft))}
                  onDismiss={() => setSuggestionDismissed(true)}
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <ThreadActionButton icon={CornerUpLeft} label="Reply" onClick={() => openComposer('reply', newestMessage)} />
              {allowForward && (
                <ThreadActionButton
                  icon={CornerUpRight}
                  label="Forward"
                  onClick={() => openComposer('forward', newestMessage)}
                />
              )}
            </div>
          </div>
        ))}

      {/* One connected thread — messages stacked with hairline dividers, like an
          email conversation, instead of separate free-floating cards. */}
      <div
        className={`overflow-hidden ${R} border border-slate-200/90 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)]`}
      >
        <div className="divide-y divide-slate-100">
          {merged.map((item, idx) =>
            item.kind === 'message' ? (
              <ThreadMessage
                key={item.id}
                message={item.message}
                conversation={conversation}
                currentAgentId={currentAgentId}
                defaultOpen={idx === newestMessageIndex}
                onAttachmentSelect={onAttachmentSelect}
                canReply={canCompose}
                allowForward={allowForward}
                onReply={(m) => openComposer('reply', m)}
                onForward={(m) => openComposer('forward', m)}
                ratingScale={ratingScale}
              />
            ) : (
              <ApprovalEventCard key={item.id} entry={item.entry} />
            ),
          )}
        </div>
      </div>
    </div>
  )
}
